import AbstractModelType from '../AbstractModelType';
import BookLocation from '../BookLocation';

// A marker represents chapter headings and subheadings in a book, and can be nested.
// It is the same as "NCXEntry" on the backend and in source epubs.
class BookMarker extends AbstractModelType {
  book;
  index;
  label;
  element;
  parentIndex;
  documentIndices = [];
  #pageLabel;
  #documents;
  #documentsForOffline;
  #pages;
  #children;
  #descendents;
  #parents;
  #topLevelMarker;

  constructor({ ...properties }) {
    super(...arguments);

    this.index = properties.index;
    this.label = properties.label?.replace(/\s+/g, ' ').trim(); // fix whitespace
    this.element = properties.element;
    this.#pageLabel = properties.page_label;

    this.parentIndex = properties.parentIndex;

    this.documentIndices =
      properties.documentIndices ?? properties.documentIndicies ?? [];
    this.documentIndices.sort((a, b) => a - b);
  }

  get bookLocation() {
    return new BookLocation({ marker: this });
  }

  get previous() {
    return this.book.markers[this.index - 1] ?? null;
  }

  get next() {
    return this.book.markers[this.index + 1] ?? null;
  }

  get documents() {
    if (this.#documents) {
      return this.#documents;
    }

    const documents = [];

    for (let documentIndex of this.documentIndices) {
      documents.push(this.book.documents[documentIndex]);
    }

    this.#documents = documents;

    return this.#documents;
  }

  get document() {
    return this.documents[0] ?? null;
  }

  get documentIndicies() {
    // alias spelling error
    return this.documentIndices;
  }

  // method to return an array of documents needed for offline use
  // this array can differ from a marker's documents in books that have documents with no associated marker, like a Cover Page or content that comes before the Table of Contents page
  get documentsForOffline() {
    if (this.#documentsForOffline) {
      return this.#documentsForOffline;
    }

    const offlineDocuments = [...this.documents];

    // if this is the first marker, attach any orphan documents
    if (this.index === 0) {
      this.book.documents.forEach((d) => {
        if (!d.markers.length) {
          offlineDocuments.push(d);
        }
      });
    }

    this.#documentsForOffline = offlineDocuments.sort(
      (documentA, documentB) => documentA.index - documentB.index,
    );

    return this.#documentsForOffline;
  }

  get pages() {
    if (this.#pages) {
      return this.#pages;
    }

    const pages = [];

    for (let page of this.book.pages) {
      if (page?.markers?.includes(this)) {
        pages.push(page);
      }
    }

    pages.sort((pageA, pageB) => pageA.index - pageB.index);

    this.#pages = pages;

    return this.#pages;
  }

  get page() {
    return this.pages[0] ?? null;
  }

  get pageLabel() {
    // page_label is not available on the marker
    if (this.#pageLabel === undefined) {
      return this.page?.label;
    }

    return this.#pageLabel;
  }

  // an array with just the children of this marker, without the children of those children
  get children() {
    if (this.#children) {
      return this.#children;
    }

    const markers = [];

    for (let marker of this.book.markers) {
      if (marker?.parentIndex === this.index && marker !== this) {
        markers.push(marker);
      }
    }

    markers.sort((markerA, markerB) => markerA.index - markerB.index);

    this.#children = markers;

    return this.#children;
  }

  // an array with the children of this marker and the marker itself
  get withChildren() {
    return [this, ...this.children];
  }

  // an array with all descendents of this marker, that is the children and all children of those children
  get descendents() {
    if (this.#descendents) {
      return this.#descendents;
    }

    this.#descendents = this.children
      .map((child) => child.withDescendents)
      .flat()
      .sort((markerA, markerB) => markerA.index - markerB.index);

    return this.#descendents;
  }

  // an array with the descendents of this marker and the marker itself
  get withDescendents() {
    return [this, ...this.descendents];
  }

  get parent() {
    if (this.parentIndex === this.index) {
      return null;
    }

    return this.book.markers[this.parentIndex] ?? null;
  }

  get parents() {
    if (this.#parents) {
      return this.#parents;
    }

    this.#parents = this.parent?.withParents ?? [];

    return this.#parents;
  }

  get withParents() {
    if (!this.parent) {
      return [this];
    }

    return [...this.parents, this];
  }

  get topLevelMarker() {
    if (this.#topLevelMarker) {
      return this.#topLevelMarker;
    }

    let marker = this;

    while (marker.parent) {
      marker = marker.parent;
    }

    this.#topLevelMarker = marker;

    return marker;
  }

  get isTopLevelMarker() {
    return this.parent === null;
  }

  setDerivedProperties() {
    return [
      this.documents,
      this.documentsForOffline,
      this.pages,
      this.children,
      this.descendents,
      this.parents,
      this.topLevelMarker,
    ];
  }
}

export default BookMarker;
